import cn from 'classnames';

import { useDictionary } from 'src/hooks/useDictionary';

import { setJumpLinkAttributes } from 'src/utils/jumpLinks';

import ArrowRight from 'assets/icons/arrow-forward.svg';

import Cta from 'components/BaseHelpers/Cta';
import RichText from 'components/BaseHelpers/RichText';
import Text from 'components/BaseHelpers/Text';
import VideoPlayer from 'components/BaseHelpers/VideoPlayer';

import { ComponentProps } from '../../../types/component-props';
import { CoreFeaturedVideoData } from '../../../types/components';

import styles from './CoreFeaturedVideo.module.scss';

export type CoreFeaturedVideoProps = ComponentProps & CoreFeaturedVideoData;

const CoreFeaturedVideo = (props: CoreFeaturedVideoProps): JSX.Element => {
  const {
    description,
    name,
    transcript,
    videoCode,
    videoFile,
    thumbnail,
    variant,
    platform,
    primaryCta,
    secondaryCta,
    jumpLinkLabel,
  } = props.fields;
  const dictionary = useDictionary();

  return (
    <div
      className={cn(styles.main, styles[variant], 'container-10')}
      {...setJumpLinkAttributes(jumpLinkLabel)}
    >
      <VideoPlayer
        className={styles['video-player']}
        videoData={videoCode}
        videoFile={videoFile}
        thumbnailImage={thumbnail}
        platform={platform}
      />
      <div className={styles.content}>
        <Text tag="h3" className={cn('heading-h5', styles.name)} field={name} />
        <RichText className={cn('body-med', styles['video-description'])} field={description} />
        {transcript.href && (
          <a
            href={transcript.href}
            className={cn(styles.transcript, 'body-sm')}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Text field={dictionary.viewTranscriptLabel} /> <ArrowRight className={styles.arrow} />
          </a>
        )}
        {variant === 'stacked' && (primaryCta?.href || secondaryCta?.href) && (
          <div className={styles.ctas}>
            {primaryCta?.href && (
              <Cta
                tag="a"
                href={primaryCta.href}
                className={cn(styles.cta, styles.primaryCta, 'primary-cta')}
                openNewTab={primaryCta.target === '_blank'}
                showIcon={false}
              >
                {primaryCta.label}
              </Cta>
            )}
            {secondaryCta?.href && (
              <Cta
                tag="a"
                href={secondaryCta.href}
                className={cn(styles.cta, styles.secondaryCta, 'secondary-cta')}
                openNewTab={secondaryCta.target === '_blank'}
                theme="filled"
                showIcon={false}
              >
                {secondaryCta.label}
              </Cta>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CoreFeaturedVideo;
